import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "ticketino-api-client";
import { getCountries, GetOrganizerProfile, UpdateOrganizerProfile } from "../../utils/api";
import { useLanguage } from "../../hooks/useLanguage";
import { useAuthGuard } from "../../hooks/useAuthGuard";
import { useOrder } from "../../hooks/useOrder";

import Header from "../components/Header";
import Footer from "../components/Footer";
import Loading from "../components/Loading";

const Profile = () => {
    // Initialize tokens from localStorage so they're available immediately
    const [regularToken, setRegularToken] = useState(localStorage.getItem("token"));
    const [buyerToken, setBuyerToken] = useState(localStorage.getItem("buyerToken"));

    const organizerId = localStorage.getItem("loggedInOrganizerId");
    const [organizerUser, setOrganizerUser] = useState(null);

    const [formError, setFormError] = useState("");
    const [formSuccess, setFormSuccess] = useState("");

    const [resources, setResources] = useState({});
    const [countries, setCountries] = useState([]);

    const { language } = useLanguage();

    let navigate = useNavigate();
    let baseUrl = process.env.REACT_APP_BASEURL_API;
    api.defaults.baseURL = baseUrl;

    const order = useOrder(baseUrl);

    useAuthGuard(language); // Check if user is authenticated

    useEffect(() => {
        if (language) {
            requestResources();
        }
    }, [language]);

    useEffect(() => {
        if (regularToken && language) {
            loadCountries();
        }
    }, [regularToken, language]);

    useEffect(() => {
        if (buyerToken && organizerId) {
            fetchOrganizerProfile();
        }
    }, [buyerToken, organizerId]);

    const requestResources = async () => {
        await axios
            .get(`form/resources/${language}`)
            .then((res) => {
                setResources(res.data?.translation);
            })
            .catch((error) => console.error(error.response.data));
    };

    const loadCountries = async () => {
        try {
            if (!regularToken) {
                throw new Error("No regular token found at LoadCountries");
            }
            let countries = await getCountries(baseUrl, "token", regularToken, language);
            const indexCH = countries.findIndex(c => c.id === 176);
            const ch = countries[indexCH];
            countries.splice(indexCH, 1);
            var indexDE = countries.findIndex(c => c.id === 229);
            const de = countries[indexDE];
            countries.splice(indexDE, 1);
            var indexAU = countries.findIndex(c => c.id === 228);
            const au = countries[indexAU];
            countries.splice(indexAU, 1);
            var indexFR = countries.findIndex(c => c.id === 59);
            const fr = countries[indexFR];
            countries.splice(indexFR, 1);
            var indexIT = countries.findIndex(c => c.id === 87);
            const it = countries[indexIT];
            countries.splice(indexIT, 1);
            countries.unshift({ id: 0, name: "---------" });
            countries.unshift(it);
            countries.unshift(au);
            countries.unshift(fr);
            countries.unshift(de);
            countries.unshift(ch);
            setCountries(countries);
        }
        catch (error) {
            console.error(error);
        }
    }

    const fetchOrganizerProfile = async () => {
        try {
            if (!buyerToken) {
                throw new Error("No buyerToken token found at fetchOrganizerProfile");
            }
            const data = await GetOrganizerProfile(baseUrl, "buyerToken", buyerToken, organizerId);
            setOrganizerUser(data);
        } catch (error) {
            console.error('Error fetching organizer profile:', error);
        }
    };

    const validateInputFields = () => {
        const genericError = resources?.ErrorPleaseFillMandatoryFields;

        if (!organizerUser) return { valid: false, error: null };
        if (!organizerUser.firstName || organizerUser.firstName.length > 70) return { valid: false, error: genericError };
        if (!organizerUser.lastName || organizerUser.lastName.length > 70) return { valid: false, error: genericError };
        if (!organizerUser.companyName || organizerUser.companyName.length > 70) return { valid: false, error: genericError };
        if (!organizerUser.street || organizerUser.street.length > 70) return { valid: false, error: genericError };
        if (!organizerUser.postCode || organizerUser.postCode.length > 70) return { valid: false, error: genericError };
        if (!organizerUser.city || organizerUser.city.length > 70) return { valid: false, error: genericError };
        if (!organizerUser.countryId) return { valid: false, error: genericError };
        if (!organizerUser.email || !/^\S+@\S+\.\S+$/.test(organizerUser.email)) return { valid: false, error: resources?.ErrorEmailNotValid };
        return { valid: true };
    }

    const saveOrganizerProfile = async (e) => {
        if (e) e.preventDefault();
        setFormError("");
        setFormSuccess("");
        const validation = validateInputFields();
        if (!validation.valid) {
            setFormError(validation.error || "Please fill all mandatory fields correctly.");
            return;
        }
        try {
            organizerUser.organizerType = null; //put fix: OrganizerType must not be submitted for Organizers of Tenant Ticketino
            await UpdateOrganizerProfile(baseUrl, "buyerToken", buyerToken, organizerUser);
            setFormSuccess(resources?._Save + " successful!");
        } catch (error) {
            setFormError(resources?._Save + " failed.");
        }
    };

    const onInputChange = (e) => {
        setOrganizerUser({ ...organizerUser, [e.target.name]: e.target.value });
    };

    return (
        <>
            <Header language={language} pagePath="login" pageTitle={resources?.Account_LoginYourAccount}
                ticketCount={order?.tickets?.length || 0} />

            <div className="page-container">

                <div className="container wrapper">

                    {/* navigation */}
                    <div className="row mb-4">
                        <div className="col-6">
                            <button className="btn-custom transparent btn-navigation" type="submit"
                                onClick={() => navigate(`/${language}/orders`)}>
                                {resources?.Account_MyOrders}
                            </button>
                        </div>
                        <div className="col-6 text-end">
                            <button className="btn-custom btn-navigation" type="submit">
                                {resources?.Account_MyProfile}
                            </button>
                        </div>
                    </div>

                    {/* inputs */}
                    {organizerUser && (
                        <>
                            <div className="form-group row">
                                <label htmlFor="salutation" className="col-md-3 col-form-label">{resources?.BuyerSalutation}</label>
                                <div className="col-md-9">
                                    <select className="form-select" value={organizerUser?.salutation || ""} name="salutation" onChange={onInputChange}>
                                        <option value=""></option>
                                        <option value={resources?.BuyerSalutationMr}>{resources?.BuyerSalutationMr}</option>
                                        <option value={resources?.BuyerSalutationMs}>{resources?.BuyerSalutationMs}</option>
                                        {language === "en" && (
                                            <option value="Mrs.">Mrs.</option>
                                        )}
                                        <option value={resources?.BuyerSalutationDiverse}>{resources?.BuyerSalutationDiverse}</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="firstName" className="col-md-3 col-form-label">{resources?.BuyerFirstname} *</label>
                                <div className="col-md-9">
                                    <input
                                        type="text"
                                        name="firstName"
                                        className="form-control"
                                        value={organizerUser?.firstName || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="lastName" className="col-md-3 col-form-label">{resources?.BuyerLastname} *</label>
                                <div className="col-md-9">
                                    <input
                                        type="text"
                                        name="lastName"
                                        className="form-control"
                                        value={organizerUser?.lastName || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="companyName" className="col-md-3 col-form-label">{resources?.BuyerCompanySchool} *</label>
                                <div className="col-md-9">
                                    <input
                                        type="text"
                                        name="companyName"
                                        className="form-control"
                                        value={organizerUser?.companyName || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="street" className="col-md-3 col-form-label">{resources?.BuyerStreet} *</label>
                                <div className="col-md-9">
                                    <input
                                        type="text"
                                        name="street"
                                        className="form-control"
                                        value={organizerUser?.street || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="postCode" className="col-md-3 col-form-label">{resources?.BuyerPostcode} *</label>
                                <div className="col-md-9">
                                    <input
                                        type="text"
                                        name="postCode"
                                        className="form-control"
                                        value={organizerUser?.postCode || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="city" className="col-md-3 col-form-label">{resources?.BuyerCity} *</label>
                                <div className="col-md-9">
                                    <input
                                        type="text"
                                        name="city"
                                        className="form-control"
                                        value={organizerUser?.city || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="countryId" className="col-md-3 col-form-label">{resources?.BuyerCountry} *</label>
                                <div className="col-md-9">
                                    <select
                                        className="form-select"
                                        aria-label=""
                                        id="countryId"
                                        name="countryId"
                                        value={organizerUser?.countryId || 176}
                                        onChange={onInputChange}
                                    >
                                        {
                                            countries.map((country) => (
                                                <option key={country.id} value={country.id}>{country.name}</option>
                                            ))
                                        }
                                    </select>
                                </div>
                            </div>

                            <div className="form-group row">
                                <label htmlFor="email" className="col-md-3 col-form-label">{resources?.BuyerEmail} *</label>
                                <div className="col-md-9">
                                    <input
                                        type="text"
                                        name="email"
                                        className="form-control"
                                        value={organizerUser?.email || ""}
                                        onChange={onInputChange}
                                    />
                                </div>
                            </div>

                            {/* infos */}
                            <div className="row mt-5">
                                <div className="col-md-12 d-flex flex-column">
                                    <span>
                                        {resources?._Click}
                                        <button
                                            type="button"
                                            style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer" }}
                                            onClick={() => navigate(`/${language}/password-reset/orders`)}
                                        >
                                            {resources?._Here}
                                        </button>
                                        {resources?.Account_MyProfilePasswordReset}
                                    </span>
                                </div>
                            </div>

                            {/* button */}
                            <div className="row mt-5">
                                <div className="col-md-12 text-center">
                                    <button
                                        className="btn-custom btn-navigation"
                                        type="button"
                                        onClick={saveOrganizerProfile}
                                    >
                                        {resources?._Save}
                                    </button>
                                </div>
                            </div>
                        </>
                    )}

                    {formError &&
                        <div className="row text-start mt-2">
                            <p className="text-red">{formError}</p>
                        </div>
                    }

                    {formSuccess &&
                        <div className="row text-start mt-2">
                            <p className="text-success">{formSuccess}</p>
                        </div>
                    }
                </div>

            </div>

            <Footer language={language} />
        </>
    )
}

export default Profile;